/**
 * Extracts the first sentence from a text string.
 * Handles edge cases like times (16.45), decimals (3.5), and abbreviations.
 * 
 * @param text - The text to extract the first sentence from
 * @returns The first sentence, or the full text if no sentence boundary is found
 * 
 * @example
 * extractFirstSentence("Förhandsvisning 23/10 kl 16.45-17.30! Välkommen.")
 * // Returns: "Förhandsvisning 23/10 kl 16.45-17.30!"
 * 
 * extractFirstSentence("Pris: 3.5 miljoner. Fin lägenhet.")
 * // Returns: "Pris: 3.5 miljoner."
 */
export function extractFirstSentence(text: string | null | undefined): string {
  if (!text) return '';
  
  // Find the first occurrence of each sentence terminator
  const exclamationIndex = text.indexOf('!');
  const questionIndex = text.indexOf('?');
  
  // Find periods that are likely sentence endings (not in times/decimals)
  // Look for period followed by space or end of string
  let periodIndex = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '.') {
      const nextChar = text[i + 1];
      const prevChar = text[i - 1];
      
      // Check if this period is likely a sentence ending:
      // - At end of string, OR
      // - Followed by whitespace AND (not preceded by digit OR followed by uppercase/end)
      if (!nextChar || 
          (nextChar === ' ' && (!prevChar || !/\d/.test(prevChar) || /[A-ZÅÄÖ]/.test(text[i + 2] || '')))) {
        periodIndex = i;
        break;
      }
    }
  }
  
  // Find the earliest sentence terminator
  const indices = [exclamationIndex, questionIndex, periodIndex].filter(i => i !== -1);
  
  if (indices.length === 0) {
    // No sentence terminator found, return full text
    return text.trim();
  }
  
  const firstTerminatorIndex = Math.min(...indices);
  return text.substring(0, firstTerminatorIndex + 1).trim();
}
